import { useContext, useState } from 'react';

import {
  faCircleCheck,
  faCircleInfo,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@repo/videa-components/src/index';

import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ExternalPatientContext } from '../constants';
import { useUpdateTrialStatus } from '../hooks';
import { TrialStatus } from '../types';

export const TrialOfferCollapsible = () => {
  const [isOpen, setIsOpen] = useState(true);

  const externalPatientContext = useContext(ExternalPatientContext);
  const { trackEvent } = useTrackEvent();
  const { t } = useLingui();
  const {
    mutateAsync: updateTrialStatus,
    isLoading,
    isError,
    isSuccess,
  } = useUpdateTrialStatus();

  const handleStartTrial = async () => {
    trackEvent(mixpanelEvents.externalTrialOfferClick, {
      ...externalPatientContext,
    });
    await updateTrialStatus({ status: TrialStatus.TRIAL });
  };

  return (
    <Collapsible
      className="vai-px-2 vai-bg-zinc-700 vai-text-white vai-p-3 vai-rounded-md"
      onOpenChange={(open) => {
        setIsOpen(open);
        trackEvent(mixpanelEvents.externalCollapsibleToggle, {
          ...externalPatientContext,
          isOpen: open,
          source: 'TRIAL_OFFER',
        });
      }}
      open={isOpen}
    >
      <CollapsibleTrigger className="vai-w-full vai-flex vai-items-center vai-justify-between vai-mb-2">
        <p className="vai-font-sans vai-font-semibold">
          {isOpen ? t`Show AI on these x-rays` : t`AI Assistance`}
        </p>
        <FontAwesomeIcon icon={isOpen ? faMinus : faPlus} />
      </CollapsibleTrigger>

      <CollapsibleContent className="vai-flex vai-flex-col vai-gap-2">
        <div
          className="vai-rounded-lg vai-overflow-hidden vai-h-28"
          style={{
            backgroundImage:
              'url(assets/images/external/image-with-findings.png)',
            backgroundSize: 'cover',
            backgroundPosition: '0 75%',
          }}
        />
        <p className="vai-font-inter vai-text-sm">
          <Trans>
            Run AI on these x-rays to see perio, resto, and endo findings.
          </Trans>
        </p>

        {isSuccess ? (
          <Alert variant="successSolid">
            <FontAwesomeIcon icon={faCircleCheck} />
            <AlertTitle>Success</AlertTitle>
            <AlertDescription>
              <Trans>
                Your trial has been started. You will see AI on the next patient
                you open.
              </Trans>
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <Button disabled={isLoading} onClick={handleStartTrial}>
              {isLoading && (
                <div className="vai-animate-spin vai-rounded-full vai-h-4 vai-w-4 vai-border-2 vai-border-t-transparent" />
              )}
              <Trans>Start 30-day no-cost trial</Trans>
            </Button>
            {isError && (
              <Alert variant="destructiveSolid">
                <FontAwesomeIcon icon={faCircleInfo} />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>
                  <Trans>
                    Something went wrong when starting your trial. Please try
                    again.
                  </Trans>
                </AlertDescription>
              </Alert>
            )}
            <p className="vai-font-inter vai-text-[12px] vai-text-zinc-200">
              <Trans>
                By accepting this evaluation product, you agree to these{' '}
                <a
                  className="vai-text-blue-500 vai-underline"
                  href="https://hsps.widen.net/s/rj8hgnltws/eservices---hs1-terms-and-conditions"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    trackEvent(mixpanelEvents.externalTermsAndConditionsClick, {
                      ...externalPatientContext,
                      source: 'TRIAL_OFFER',
                    });
                  }}
                >
                  terms and conditions
                </a>{' '}
                and that you will not charge, or be reimbursed by any federal
                program (e.g., Medicare, Medicaid, Tricare of Children&apos;s
                Health Insurance Program), or any third party for such
                evaluation product or its use. The evaluation trial period will
                last for 30 days and will automatically cease at the end of such
                period.
              </Trans>
            </p>
          </>
        )}
      </CollapsibleContent>

      {!isOpen && (
        <p className="vai-font-inter vai-text-sm">
          <Trans>Expand to learn more</Trans>
        </p>
      )}
    </Collapsible>
  );
};
